import { useState, useCallback } from "react";
import { Form, message, notification } from "antd";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import { authAxios } from "../../../utils/session_utils";
import { useLocation } from "react-router-dom";
import { FilterData } from "../../../utils/types";
import { AxiosError } from "axios";
import { borrowers, borrowerTerritories, borrowerTerritoryChannel, lenderPlatforms } from "./rawData";

export interface FileUploadFilter {
  is_borrowed: string;
  domain: string;
  platform: string;
  territory?: string;
  month?: string;
  year?: string;
}

const useViewershipUploadDataHook = () => {
  const { pathname } = useLocation();

  const [loading, setLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [file, setFile] = useState<RcFile | null>(null);
  const [isFiltersSet, setIsFiltersSet] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true); // Modal open by default
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [processingStatus, setProcessingStatus] = useState('');
  const [form] = Form.useForm();
  const [domain, setDomain] = useState<string>('');
  const [platformKey, setPlatformKey] = useState<string>('');
  const [partner, setPartner] = useState('');
  const [territory, setTerritory] = useState('');
  const [channel, setChannel] = useState('');
  const [showTerritory, setShowTerritory] = useState<boolean>(false);
  const [showMonth, setShowMonth] = useState<boolean>(false);
  const [showYear, setShowYear] = useState<boolean>(false);
  const [showChannel, setShowChannel] = useState<boolean>(false);
  const [showHOVField, setShowHOVField] = useState<boolean>(false);
  const [showViewershipPartner, setShowViewershipPartner] = useState<boolean>(false);
  const [isBorrowedViewership, setIsBorrowedViewership] = useState<boolean>(false);
  // Options
  const platformOptions: { [key: string]: string[] } = {
    'Distribution Partners-borrowed': lenderPlatforms[`${partner}-${territory}-${channel}`] || [],
    'Distribution Partners-direct': ['Wurl', 'Pluto', 'Youtube'],
  };

  // Replace the hardcoded borrowerPartnerOptions with the imported borrowers array
  // const borrowerPartnerOptions = ['DirecTV', 'Freevee Linear', 'Fubo', 'Peacock Linear', 'Philo', 'SlingTV', 'Telus', 'Tubi Linear'];
  const borrowerPartnerOptions = borrowers;

  const territoryOptions = isBorrowedViewership ? borrowerTerritories[partner] : ['International', 'United States', 'United Kingdom', 'Canada', 'Latin America', 'Brazil', 'India', 'Mexico', 'Denmark', 'Norway', 'Sweden'];
  const channelOptions = isBorrowedViewership ? (borrowerTerritoryChannel[partner] ?? [])[territory] : ['Nosey', 'Confess by Nosey', 'Judge Nosey', 'Nosey Escalando', 'Presented by Nosey'];

  // UI logic
  const handleFormChange = () => {
    const { domain, is_borrowed, platform, partner, territory, channel } = form.getFieldsValue();
    const platformKey = domain ? `${domain}-${is_borrowed}` : undefined;
    const isBorrowed = is_borrowed === 'borrowed'
    const showTerritory = platform === 'Pluto' || platform === 'Youtube' || isBorrowed;
    const showMonth = platform === 'Pluto' || platform === 'Youtube' || isBorrowed;
    const showYear = platform === 'Pluto' || platform === 'Youtube' || isBorrowed;
    const showChannel = is_borrowed === 'borrowed';
    const showViewershipPartner = is_borrowed === 'borrowed';
    const showHOVField = is_borrowed === 'borrowed';
    setDomain(domain);
    setPartner(partner);
    setTerritory(territory);
    setChannel(channel);
    setPlatformKey(platformKey || '');
    setShowTerritory(showTerritory);
    setShowMonth(showMonth);
    setShowYear(showYear);
    setShowChannel(showChannel);
    setShowHOVField(showHOVField);
    setShowViewershipPartner(showViewershipPartner);
    if (isBorrowed) {
      setIsBorrowedViewership(true);
      setPlatformKey(`${domain}-${is_borrowed}`)
      form.setFieldsValue({
        domain
      });
    } else {
      setIsBorrowedViewership(false);
    }
  }

  // Handlers
  const closeFilterModal = useCallback(() => {
    form.resetFields();
    setIsModalOpen(false)
  }, []);
  const openFilterModal = useCallback(() => {
    form.resetFields();
    setIsModalOpen(true);
  }, [form]);

  const handleIsFiltersSet = useCallback((value: boolean) => setIsFiltersSet(value), []);

  const handleSetFilters = useCallback(async () => {
    try {
      const {domain, partner, channel, territory, month, year, hov, quarter, lender, platform, is_borrowed} = await form.validateFields(["domain", "partner", "channel", "territory", "month", "year", "hov", "quarter", "lender", "platform", "is_borrowed"]);
      if (is_borrowed === 'borrowed') {
        setLoading(true);
        const response = await authAxios({
          method: "POST",
          url: `${process.env.REACT_APP_BACK_END_API}/register-data/borrowed-partner-data`,
          data: {domain, partner, channel, territory, month, year, quarter, platform: lender, hov, type: 'Viewership'},
        });
        message.success(response.data.message);
        setLoading(false);
      }
      else {
        setFilterData({
          domain,
          platform,
          territory,
          month,
          year,
          is_borrowed,
        });
        handleIsFiltersSet(true);
        setIsModalOpen(false);
        handleFileRemove();
      }
    } catch (error: any) {
      console.log("🚀 ~ handleSetFilters ~ error:", error)
      if (error instanceof AxiosError) {
        message.error(error.response?.data?.message || "Something went wrong");
      } else {
        message.error("Something went wrong");
      }
    }
  }, [form, handleIsFiltersSet]);



  // File upload logic
  const handleFileRemove = useCallback(() => {
    setFile(null);
    setFileList([]);
    return true;
  }, []);

  const beforeUpload = useCallback((file: RcFile) => {
    setFileList([file]);
    setFile(file);
    return false; // Prevent automatic upload
  }, []);

  const uploadProps: UploadProps = {
    accept: ".csv",
    onRemove: handleFileRemove,
    beforeUpload: beforeUpload,
    fileList,
  };

  // Polling for backend processing status
  const pollForStatus = useCallback((operationId: string) => {
    let elapsedTime = 0;
    const checkStatus = async () => {
      const config = {
        method: 'get',
        url: `${process.env.REACT_APP_BACK_END_API}/register-data/operation-status/${operationId}`,
      };
      
      try {
        const statusResponse = await authAxios(config);
                
        // Extract the actual status - handle both old format (string) and new format (job object)
        const actualStatus = typeof statusResponse.data.status === 'object' 
          ? statusResponse.data.status.status 
          : statusResponse.data.status;
                
        if (actualStatus === 'COMPLETED' || actualStatus === 'success') {
          console.log(`🎉 Processing completed successfully!`);
          message.success('File processed successfully');
        } else if (actualStatus === 'PROCESSING' || actualStatus === 'processing') {
          console.log(`⚙️ Still processing... continuing to poll`);
          if (elapsedTime < 180000) {
            setTimeout(checkStatus, 5000);
            elapsedTime += 5000;
          } else {
            console.log(`⏰ Timeout reached after ${elapsedTime}ms`);
            message.warning('Timeout: Processing taking too long');
          }
        } else if (actualStatus === 'FAILED' || actualStatus === 'CANCELLED') {
          console.log(`❌ Processing failed with status: ${actualStatus}`);
          message.error('Processing failed');
        } else {
          console.log(`❓ Unknown status: ${actualStatus} - treating as failure`);
          message.error('Processing failed');
        }
      } catch (error) {
        console.error(`💥 [Viewership Status Check] Error for operation ${operationId}:`, error);
        console.error(`📡 Request config:`, config);
        message.error('Failed to check processing status');
      }
    };
    checkStatus();
  }, []);

  // Upload handler
  const handleUpload = useCallback(async (filterData: any) => {
    if (!file) {
      message.error("Please insert a file first!");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      const userEmail = window.sessionStorage.getItem("mvmgsid") || "";
      formData.append("file", file as RcFile);
      formData.append("filterData", JSON.stringify(filterData));
      formData.append("userEmail", userEmail);
      const response = await authAxios({
        method: "POST",
        url: `${process.env.REACT_APP_BACK_END_API}/register-data/upload`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 202) {
        message.info(response.data.message);
        notification.success({
          message: "Processing started",
          description: `The file is being processed by Snowflake. You can track the progress in the Snowflake dashboard using this file. ${response.data.filename}`,
        });
        const { operationId } = response.data;
        setProcessingStatus("processing");
        pollForStatus(operationId);
      } else {
        message.error("Something went wrong");
      }
    } catch (error) {
      message.error("An error occurred during upload.");
    } finally {
      setLoading(false);
    }
  }, [file, pollForStatus]);

  // Wrapper to validate filterData before upload
  const handleUploadWrapper = useCallback(async () => {
    if (
      filterData &&
      filterData.platform &&
      filterData.domain &&
      (filterData.platform === 'Wurl' || (filterData.month && filterData.year && filterData.territory))
    ) {
      await handleUpload(filterData);
    } else {
      message.error("Filter data is incomplete. Cannot proceed with upload.");
    }
  }, [filterData, handleUpload]);

  return {
    pathname,
    loading,
    file,
    handleUpload,
    uploadProps,
    isFiltersSet,
    setIsFiltersSet,
    isModalOpen,
    setIsModalOpen,
    filterData,
    setFilterData,
    processingStatus,
    setProcessingStatus,
    handleUploadWrapper,
    showTerritory,
    showMonth,
    showYear,
    handleFormChange,
    handleSetFilters,
    platformOptions,
    territoryOptions,
    closeFilterModal,
    form,
    openFilterModal,
    domain,
    platformKey,
    channelOptions,
    showChannel,
    showHOVField,
    showViewershipPartner,
    borrowerPartnerOptions
  };
};

export default useViewershipUploadDataHook;
